import { APS_URL, GPT_URL } from 'constants/appConstants';
import { injectScriptToHead } from 'helpers/HtmlHelpers';
import { isDesktop } from 'helpers/ClientUtils';
import { logError } from 'middleware/logger';

export const PDP_NARROW_MID = 'PDP-NARROW-MID';
export const PDP_NARROW_TOP = 'PDP-NARROW-TOP';
export const SRP_NARROW_MID = 'SRP-NARROW-MID';
export const SRP_NARROW_TOP = 'SRP-NARROW-TOP';
export const PDP_NARROW_BOTTOM = 'PDP-NARROW-BOTTOM';
export const SRP_NARROW_BOTTOM = 'SRP-NARROW-BOTTOM';
export const PDP_WIDE_BOTTOM = 'PDP-WIDE-BOTTOM';
export const PDP_WIDE_TOP = 'PDP-WIDE-TOP';
export const SRP_WIDE_BOTTOM = 'SRP-WIDE-BOTTOM';
export const SRP_WIDE_TOP = 'SRP-WIDE-TOP';
export const PDP_WIDE_MID = 'PDP-WIDE-MID';
export const SRP_WIDE_MID = 'SRP-WIDE-MID';
export const TEST = 'test';

// method for getting dimensions based on slot
export const getAdDimensions = slot => {
  switch (slot) {
    case PDP_WIDE_BOTTOM:
    case PDP_WIDE_TOP:
    case SRP_WIDE_BOTTOM:
    case SRP_WIDE_TOP:
    case TEST:
      return [728, 90];
    case PDP_NARROW_MID:
    case PDP_NARROW_TOP:
    case SRP_NARROW_MID:
    case SRP_NARROW_TOP:
      return [320, 50];
    case PDP_NARROW_BOTTOM:
    case PDP_WIDE_MID:
    case SRP_NARROW_BOTTOM:
      return [300, 250];
    case SRP_WIDE_MID:
      return [160, 600];
    default:
      logError(`No dimensions named ${slot}.`);
      return null;
  }
};

// map for slots to device
export const platformVisibility = {
  desktop: [
    PDP_WIDE_BOTTOM,
    PDP_WIDE_TOP,
    SRP_WIDE_BOTTOM,
    SRP_WIDE_TOP,
    PDP_WIDE_MID,
    SRP_WIDE_MID,
    TEST
  ],
  mobile: [
    PDP_NARROW_MID,
    PDP_NARROW_TOP,
    SRP_NARROW_MID,
    SRP_NARROW_TOP,
    PDP_NARROW_BOTTOM,
    SRP_NARROW_BOTTOM
  ]
};

export const defineSlotData = ({ slot, device, networkId }, cb, win = window) => {
  const { googletag } = win;
  const shouldFetchBid = platformVisibility[device]?.includes(slot);

  if (shouldFetchBid) {
    const slotName = `/${networkId}/${slot}`;
    const dimensions = getAdDimensions(slot);
    googletag.cmd.push(() => {
        googletag.defineSlot(slotName, [dimensions], slot)?.addService(googletag.pubads());
    });

    if (cb) {
      cb({ slotName, dimensions });
    }
  }
};

export const initializeAps = ({ sourceId, networkId, slots, device }, win = window) => {
  const { apstag, googletag } = win;

  slots.forEach(slot => {
    defineSlotData({ slot, device, networkId }, null, win);
  });

  googletag.cmd.push(() => {
    googletag.pubads().disableInitialLoad();
    googletag.pubads().enableSingleRequest();
    googletag.pubads().collapseEmptyDivs();
    googletag.enableServices();
  });

  apstag.init({
    pubID: sourceId,
    adServer: 'googletag',
    bidTimeout: 2e3
  });

  injectScriptToHead({
    src: GPT_URL
  });

  injectScriptToHead({
    src: APS_URL,
    id: 'apsScript',
    onload: () => {
      fetchBids({ slots, networkId, device });
    }
  });
};

export const fetchBids = ({ slots, networkId, device }, win = window) => {
  const { apstag, googletag } = win;

  const slotData = [];

  slots.forEach(slot => {
    defineSlotData({ slot, device, networkId }, ({ slotName, dimensions }) => {
      slotData.push({
        slotID: slot,
        slotName,
        sizes: [dimensions]
      });
    }, win);
  });

  apstag.fetchBids({ slots: slotData }, () => {
    // set apstag bids, then trigger the first request to DFP
    googletag.cmd.push(() => {
      apstag.setDisplayBids();
      googletag.pubads().refresh();
    });
  });

  slots.forEach(slot => {
    googletag.cmd.push(() => {
      googletag.display(slot);
    });
  });
};

export const fireApsAds = ({ marketplace, slots }) => {
  const { ads: { networkId, sourceId } = {} } = marketplace;
  const device = isDesktop() ? 'desktop' : 'mobile'; // set `device` after component has been mounted
  try {
    // if script has already been injected, re-fire ad initialization
    if (document.getElementById('apsScript')) {
      fetchBids({ networkId, slots, device });
    } else {
      initializeAps({ networkId, sourceId, slots, device }); // otherwise inject scripts and fire ad initialization
    }
  } catch (e) { /* dont blow up if third party script fails */ }
};
